import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Toaster, toast } from "sonner";
import { ArrowLeft, Check, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useLogo } from "@/context/AssetsContext";
import { SERVICE_CATEGORIES } from "@/data/content";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const FIELDS = [
  ["business_name", "Business Name", true],
  ["contact_name", "Owner / Contact Name", true],
  ["email", "Email", true],
  ["phone", "Phone Number", true],
  ["address", "Business Address", false],
  ["city", "City", true],
  ["state", "State", true],
  ["zip_code", "ZIP Code", false],
  ["website", "Website (optional)", false],
  ["service_radius", "Service Area / Radius", false],
  ["employees", "Number of Employees / Technicians", false],
  ["years_in_business", "Years in Business", false],
  ["google_profile", "Google Business Profile URL (optional)", false],
];

export default function Providers() {
  const logo = useLogo();
  const [form, setForm] = useState({});
  const [cats, setCats] = useState([]);
  const [company, setCompany] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleCat = (c) => setCats((p) => (p.includes(c) ? p.filter((x) => x !== c) : [...p, c]));

  const submit = async (e) => {
    e.preventDefault();
    const required = ["business_name", "contact_name", "email", "phone", "city", "state"];
    if (required.some((r) => !form[r])) { toast.error("Please fill in all required fields."); return; }
    if (!cats.length) { toast.error("Select at least one service category."); return; }
    setLoading(true);
    try {
      await api.post("/providers/apply", { ...form, service_categories: cats, company });
      setDone(true);
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFC]">
      <Toaster position="top-center" richColors />
      <header className="sticky top-0 z-40 glass">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2.5" data-testid="provider-logo-home">
            <img src={logo} alt="SOSConnect AI" className="h-9 w-9 object-contain" />
            <span className="text-[17px] font-bold tracking-tight">SOSConnect <span className="text-brand">AI</span></span>
          </Link>
          <Link to="/" className="flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900"><ArrowLeft className="h-4 w-4" /> Back</Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-16">
        {done ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-3xl border border-black/5 bg-white p-12 text-center shadow-sm" data-testid="provider-success">
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-ai text-white"><Check className="h-8 w-8" strokeWidth={3} /></div>
            <h1 className="mt-6 text-3xl font-bold tracking-tighter text-neutral-900">Application received.</h1>
            <p className="mx-auto mt-3 max-w-md text-neutral-500">We'll contact you with next steps as SOSConnect AI prepares to launch in your area.</p>
            <Link to="/" className="mt-8 inline-block rounded-full bg-brand px-7 py-3.5 text-base font-semibold text-white hover:bg-brand-dark">Back to home</Link>
          </motion.div>
        ) : (
          <>
            <div className="mb-10">
              <span className="inline-flex items-center gap-2 rounded-full bg-brand/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-brand">Provider Network</span>
              <h1 className="mt-5 text-4xl font-bold tracking-tighter text-neutral-900 sm:text-5xl">Join the SOSConnect AI provider network.</h1>
              <p className="mt-4 max-w-xl text-lg text-neutral-500">Apply today and be among the first businesses available when SOSConnect AI launches in your area.</p>
            </div>

            <form onSubmit={submit} className="rounded-3xl border border-black/5 bg-white p-6 shadow-sm sm:p-8" data-testid="provider-form">
              <input type="text" value={company} onChange={(e) => setCompany(e.target.value)} className="hidden" tabIndex={-1} aria-hidden />
              <div className="grid gap-4 sm:grid-cols-2">
                {FIELDS.map(([k, label, req]) => (
                  <div key={k} className={k === "address" ? "sm:col-span-2" : ""}>
                    <label className="mb-1.5 block text-sm font-medium text-neutral-700">{label}{req && <span className="text-sos"> *</span>}</label>
                    <Input value={form[k] || ""} onChange={set(k)} className="h-12 rounded-xl" data-testid={`provider-${k}`} />
                  </div>
                ))}
              </div>

              <div className="mt-6">
                <label className="mb-2 block text-sm font-medium text-neutral-700">Service Categories <span className="text-sos">*</span></label>
                <div className="flex flex-wrap gap-2">
                  {SERVICE_CATEGORIES.map((c) => (
                    <button key={c} type="button" onClick={() => toggleCat(c)}
                      className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${cats.includes(c) ? "border-brand bg-brand text-white" : "border-black/10 text-neutral-600 hover:border-brand/40"}`}
                      data-testid={`provider-cat-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{c}</button>
                  ))}
                </div>
              </div>

              <div className="mt-6">
                <label className="mb-1.5 block text-sm font-medium text-neutral-700">Additional Information (optional)</label>
                <Textarea value={form.additional_info || ""} onChange={set("additional_info")} rows={4} className="rounded-xl" data-testid="provider-additional" />
              </div>

              <Button type="submit" disabled={loading} className="mt-8 h-13 w-full rounded-xl bg-brand py-3.5 text-base font-semibold text-white hover:bg-brand-dark active:scale-[0.98]" data-testid="provider-submit">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Apply to Join"}
              </Button>
            </form>
          </>
        )}
      </main>
    </div>
  );
}
